import { CheckableType, ElementOf, tuple } from '../common/props';
import {
  CheckStatus,
  IData,
  RowRenderProps,
} from '../virtual-data/VirtualData';
import {
  DataRequestVO,
  DataResponseVO,
  DataSortVO,
} from '@mc/data-filter-react';
import { FilterType } from '../filter/types';
import { CSSProperties, ReactNode, RefObject } from 'react';
import { ExtendedFilter, FilterDef } from '../filter/Filters';
import { Index, IndexRange, InfiniteLoader } from 'react-virtualized';
import { LoadDataApiError } from '@mc/axios-api';
import { RowsDataConfig } from '../rows-data/useRowsData';
import * as React from 'react';
import { LinkProps } from 'react-router-dom';
import {
  ClearFilterConfig,
  ColumnInfo,
  FieldValuesFilterData,
} from '../rows-data/types';
import { DataFieldValuesRequestVO } from '@mc/data-filter-react';

export type FinalTableAlignmentType = ElementOf<typeof Alignment>;
const Alignment = tuple('left', 'center', 'right');

export type FinalTableGetRowPropsType<DATA> = (props: {
  row: DATA | null | undefined;
  rowIndex: number;
}) =>
  | {
      /**
       * Pokud se má  vyrenderovat jako link (a href), předá se tato properta a kždý řádek bude jako a href.
       */
      link?: LinkProps;
      onClick?: (
        event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
      ) => any;
      className?: string;
      style?: CSSProperties;
    }
  | undefined;

export type FinalTableColumnType = ElementOf<typeof ColumnTypes>;
const ColumnTypes = tuple(
  '_check',
  '_group',
  'text',
  'integer',
  'decimal',
  'date',
  'checkBox',
  'currency',
  'currencyDecimal',
  'dateTime',
  'boolean',
  'action'
);

export type FinalTableNumberProps = {
  fractionDigits?: number;
  noThousandsSeparator?: boolean;
};

export type FinalTableTimeProps = {
  withSeconds?: boolean;
};

export type FinalTableColumnFilters<FORM> = {
  formFilter: FORM;
  setFilter: (formData: FORM, queryData: FORM) => void;
  type?: FilterType;
};

export const FinalTableTypes = tuple(
  /**
   * Načte se seznam všech id a při zobrazení řádku se pro id donačítají data řádků.
   */
  'ids',
  /**
   * ZATIM NEPODPOROVANO
   * Načte se jen počet záznamů a při zobrazení řádku se pro zobrazovaný rozsah indexu (index záznamu v celkovém počtu) donačítají data řádků.
   * Při zapnutí seskupení se tabulka změní na typ 'ids'.
   */
  'index',
  /**
   * Načtou se rovnou data první stránky a při přepnutí na další se načtou data pro další stránku.
   * Při zapnutí seskupení se tabulka změní na typ 'ids'.
   */
  'pagination'
);
export type FinalTableType = ElementOf<typeof FinalTableTypes>;

/**
 * Typy agregací.
 */
export const TblAggregateTypes = tuple(
  'none',
  'sum',
  'avg',
  'min',
  'max',
  'count'
);
export type TblAggregateType = ElementOf<typeof TblAggregateTypes>;

/**
 * Objekt definující agregaci.
 */
export type DataAggregateVO = {
  /**
   * Dle jakého sloupce je agregováno.
   */
  field: string;

  /**
   * Agregační funkce.
   */
  aggregate: TblAggregateType;
};

/**
 * Objekt definující seskupení - group by.
 */
export type DataGroupVO = {
  /**
   * Dle jakého sloupce je agregováno.
   */
  field: string;
};

export const GROUP_PREFIX = '»';
export const FOOTER = '»FOOTER«';

export type SortInfo = {
  sort: DataSortVO;
  depth: number;
  grouped: boolean;
};

export type LoadedMap<DATA> = { [key: string]: DATA | null };

export enum GroupStatus {
  EXPANDED = 'E',
  COLLAPSED = 'C',
  LOADING = 'L',
}

export enum FinalTableEventType {
  INVALIDATE_ALL = 'INVALIDATE_ALL',
  INVALIDATE_GROUP_ROWS = 'INVALIDATE_GROUP_ROWS',
  INVALIDATE_DATA_ROWS = 'INVALIDATE_DATA_ROWS',
  INVALIDATE_FOOTER = 'INVALIDATE_FOOTER',
  INSERT_ROWS = 'INSERT_ROWS',
  REFETCH = 'REFETCH',
}

export type FinalTableEvent = {
  type: FinalTableEventType;
  autoInvalidate: boolean;
  ids?: any[];
};

export type CommonFinalTableProps<DATA extends IData<ID>, FILTER, ID> = {
  removeGroup: (field: string) => void;
  addGroup: (field: string) => void;
  grouping: DataGroupVO[];
  aggregate: DataAggregateVO[];
  setAggregate: (field: string, aggregate?: DataAggregateVO) => void;
  getAggregate: (field?: string) => DataAggregateVO | undefined;
  isGrouping: () => boolean;
  isGroupByIndex: (rowIndex: number) => boolean;
  isGroupSelectable: (rowIndex: number) => boolean;
  getNextGroupHeaderRowIndex: (rowIndex: number) => number;
  getGroupItemIds: (rowIndex: number) => ID[];
  getSelectedGroupItemIds: (rowIndex: number) => ID[];
  isGroupChecked: (rowIndex: number) => boolean;
  checkGroup: (rowIndex: number, checked: boolean) => void;
  getGroupStatusByIndex: (rowIndex: number) => GroupStatus;
  toggleExpand: (rowIndex: number) => void;
  getDepthByIndex: (rowIndex: number) => number;

  errors: LoadDataApiError[];

  columns: ColumnInfo[];
  setColumns: (columns: ColumnInfo[]) => void;

  groupColumnWidth?: number;
  setGroupColumnWidth: (columns: number) => void;

  checkedIds: ID[];
  setCheckedIds: (ids: ID[]) => void;

  lastVisitedRowIndex?: number;
  setLastVisitedRowIndex?: (id: number) => void;

  showFooter: boolean;
  setShowFooter: (showFooter: boolean) => void;

  setProfile: (profile?: FinalTableProfileData) => void;

  sort: DataSortVO[];
  setSort: (sort: DataSortVO[]) => void;
  getSortInfo: (column: FinalTableColumn<DATA>) => SortInfo | undefined;
  toggleSort: (column: FinalTableColumn<DATA>, shiftKey: boolean) => void;
  getColumnsSortInfo: () => SortInfo[];

  formFilter: FILTER;
  setFilter: (formData: FILTER, queryData: FILTER) => void;
  filterVisibleFields?: string[];
  setFilterVisibleFields: (fields: string[]) => void;
  getClearFilter: (config: ClearFilterConfig) => any;

  fieldValuesFilter: FieldValuesFilterData;
  setFieldValuesFilter: (fieldValuesFilter: FieldValuesFilterData) => void;

  searchFieldValues: (
    fieldValuesRequestVO: DataFieldValuesRequestVO
  ) => Promise<DataResponseVO<DATA>>;

  /**
   * Zobrazované řádky - seznam id tak jak je chceme zobrazit, včetně "virtuálních id" pro skupiny.
   */
  ids?: (ID | string)[];

  loading: boolean;

  /**
   * Jestli na daném indexu je skupina nebo klasický řádek.
   * Možná by šlo řešit jen přes prefix, ale bude to zase pomalejší.
   */
  group?: boolean[];

  /**
   * Hloubka zanoření.
   */
  depth?: number[];

  /**
   * Celkový počet záznamů pro daný dotaz - v případě grouping je zde počet záznamů skupin, nikoli počet datových záznamů pod tím, ten je vracen v totalDataElements
   */
  totalElements?: number;
  /**
   * Celkový počet datových řádků, pokud není grouping, je zde v, v případě grouping je zde celkový počet datových řádků pod tím.
   */
  totalDataElements?: number;

  loadMoreRows: (props: IndexRange) => Promise<any>;
  onSectionRendered: (props: SectionRange) => void;
  isRowLoaded: (info: Index) => boolean;
  getRowByIndex: (rowIndex: number) => undefined | null | DATA;
  getFooter: () => undefined | null | DATA;
  getRowRenderProps: (rowIndex: number) => RowRenderProps<ID, DATA>;

  rerender: number;
  infiniteLoaderRef: RefObject<InfiniteLoader>;
};

export type FinalTableProps<
  DATA extends IData<ID>,
  FILTER,
  ID,
> = CommonFinalTableProps<DATA, FILTER, ID> & {
  config: FinalTableConfig<DATA, FILTER, ID>;
};

export type SectionRange = {
  startIndex: number;
  stopIndex: number;
};

export type FinalTableFiltersDef = {
  filtersDef: FilterDef[];
  type?: FilterType;
  /**
   * Pokud je true, tak se u u položek dá filtrovat vyplněnost/nevyplněnost hodnoty. Tímto se zapíná globálně pro všechny (kromě checkbox). Filtrování lze pro jednu položku přímo na definici položky vypnout.
   * @deprecated nahrazeno extendedFilter
   */
  filterByFilled?: boolean;
  /**
   * Extended nastavení filtru, např. pro přepínání mezi "je vyplněno" a "není vyplněno", nebo přepínání mezi "=" a "!=".
   * Lze zapnout globálně pro všechny položky na Filters a případně pro jednu položku zase vypnout.
   */
  extendedFilter?: ExtendedFilter;
};

export type FinalTableCellProps<DATA extends object, VALUE extends unknown> = {
  /**
   * Index řádku.
   */
  rowIndex: number;
  /**
   * Index sloupce.
   */
  columnIndex: number;
  /**
   * Data řádku.
   */
  row: DATA;
  /**
   * Definice sloupce.
   */
  column: FinalTableColumn<DATA>;
  /**
   * Je k dispozici jen u tabulek, které mají data na klientovi, např. základní Table
   */
  rows?: DATA[];
  /**
   * Hodnota získaná přes accessor z hlavního datového objektu.
   * V případě group sloupce je to hodnota získaná z agregovaného objektu.
   */
  value?: VALUE;
  /**
   * Naformátovaná value dle typu sloupce.
   */
  formattedValue?: VALUE;
};

export type FinalTableCellRender<
  D extends object = {},
  VALUE extends unknown = {},
> = (props: FinalTableCellProps<D, VALUE>) => React.ReactNode | string;

export type FinalTableTitleRender<
  D extends object = {},
  VALUE extends unknown = {},
> = (props: FinalTableCellProps<D, VALUE>) => string | React.ReactNode;

export type FinalTableColumn<DATA extends object = {}> = {
  /**
   * Hlavička sloupce.
   */
  header?: string | ReactNode;
  /**
   * Title pro zobrazení v tooltipu, pokud není uveden, použije se header (pokud je typu string).
   */
  headerTitle?: string;
  /**
   * Je to row check sloupec.
   */
  check?: boolean;
  /**
   * Cesta k načtení dat, může být použit i zápis např. 'adresa.ulice'. Hodnota se používá i pro sort. Název sort lze přetížit uvedením stributu id.
   */
  accessor?: string;
  /**
   * Název atributu, používá se např. u sort (když se řadí dle tohoto sloupce, pošle se jako sort hodnota accessor, pokud je uvedeno id, tak se použije hodnota s id, jedná se o případ, kdy do accessor např. potřebujeme ulice.nazev).
   */
  id?: string;
  /**
   * Typ sloupce.
   */
  type?: FinalTableColumnType;
  /**
   * Typ sloupce v případě že zobrazuje agregační hodnoty, pokud není uvedeno, použije se type, s tím, že pokud je type celé číslo, dá se do aggregatedType 'decimal' a aggregatedNumberProps.fractionDigits = 0.
   */
  aggregatedType?: FinalTableColumnType;
  /**
   * Pokud je sloupec typu akce, tak zde mje počet akčních tlačítek - berou se v ůvahu POUZE malá tlačítka typu icon a předpokládá se, že jsou v ButtonToolbar (pokud je jich více než jedno). Výchozí hodnota je 1.
   */
  actionSize?: number;
  /**
   * Filtry pro sloupeček tabulky.
   */
  filters?: FilterDef[];
  /**
   * Je možné filtrovat dle hodnot ve sloupci? Výchozí je true pokud se jedná o sloupec typu, který to podporuje (např. text, číslo apod.)
   */
  filterable?: boolean;
  /**
   * Zarovnání obsahu sloupce, pokud není uvedeno, řídí se dle typu sloupce.
   */
  align?: FinalTableAlignmentType;
  /**
   * Doplňující formát pro čísla.
   */
  numberProps?: FinalTableNumberProps;
  /**
   * Doplňující formát pro čísla pro agregované hodnoty.
   */
  aggregatedNumberProps?: FinalTableNumberProps;
  /**
   * Doplňující formát pro čas.
   */
  timeProps?: FinalTableTimeProps;
  /**
   * Class name.
   */
  className?: string;
  /**
   * Renderer pro buňku.
   */
  cell?: FinalTableCellRender<DATA>;
  /**
   * Renderer pro title buňky, standardně bez uvedení se do title dává hodnota buňky, pokud je ale hodnota "custom" pomocí cell, tak se do title dá reálná hodnota přes accessor (což je např. id a to není dobře) a pak je vhodné použít title.
   */
  title?: FinalTableTitleRender<DATA>;
  /**
   * Renderer pro agregovanou buňku, pokud není uvedeno, použije se cell.
   * Stejný renderer je použit i pro footer.
   */
  aggregatedCell?: FinalTableCellRender<DATA>;
  /**
   * Šířka sloupce, buď číslo (ať už number nebo string) jako počet pixelů, nebo precentuelně jako např. "22%".
   */
  width?: number | string;
  /**
   * Je povolené řazení? Výchozí je true.
   */
  sortable?: boolean;
  /**
   * Je možné dle něj seskupit? Výchozí je true.
   */
  groupable?: boolean;
  /**
   * Přetížená hodnota pro popisek buňky.
   */
  columnTitle?: string;
};

export type FinalTableComputedColumn<D extends object = {}> = Omit<
  FinalTableColumn<D>,
  'sortable' | 'groupable' | 'type'
> & {
  /**
   * Typ sloupce.
   */
  type: FinalTableColumnType;
  /**
   * Pokud se jedná o seskupení sloupců, jsou zde definice těchto sloupců.
   */
  groupColumns?: FinalTableComputedColumn<D>[];
  /**
   * Je to sloupec ve kterém jsou seskupené sloupce?
   */
  group?: boolean;
  /**
   * Je povolené řazení?
   */
  sortable: boolean;
  /**
   * Je možné dle něj seskupit?
   */
  groupable: boolean;
};

export type FinalTableCommonRenderer<ID extends unknown> = {
  headerProps: {
    checkedStatus: CheckStatus;
    onToggleCheckAll: () => void;
    checkable: CheckableType;
    disabled: boolean;
  };
  checkAll: (allChecked: boolean) => void;
  checkedIds: ID[];
};

export const DEFAULT_FIELD_VALUES_FILTER_MAX_FETCHED_ITEMS = 100;

// @ts-ignore
export type CommonFinalTableConfig<DATA extends IData<ID>, FILTER, ID> = {
  /**
   * Klíč / Identifikace pro ukládání nastavení (např. filtry, profily apod.).
   */
  settingsKey?: string;

  /**
   * Pokud je true, tak se tabulka bude snažit automaticky ukládat nastavení (např. šířky sloupců, viditelnost dloupců, viditelnost filtrů apod.). Výchozí je false.
   */
  autoSaveSettings?: boolean;

  /**
   * @deprecated pokud je true, tak se budou načítat jen data pro viditelné sloupce, deprecated protože se časem tento atribut změní implicitně na true a zapnuto vždy - až se otestuje.
   */
  readOnlyVisibleColumns?: boolean;

  /**
   * Maximální počet řádků které se mají zobrazit při zobrazení hodnot sloupce pro možnost jejich filtrování (checkboxy hodnot pro sloupce kdy omezujeme řádky dle hodnot tohoto sloupce). Výchozí hodnota je 100.
   */
  fieldValuesFilterMaxFetchedItems?: number;

  /**
   * Definice filtrů nad tabulkou.
   */
  filters?: FinalTableFiltersDef;

  /**
   * Definice sloupců tabulky.
   */
  columns: FinalTableColumn<DATA>[];

  /**
   * Povolení zaškrtávání řádků.
   */
  checkable?: boolean;

  /**
   * Pokud true, je možné pomocí kávesy shift provádět řazení dle více sloupců. Výchozí je true.
   */
  multisort?: boolean;

  /**
   * Zda u tabulky zobrazovat řazení dle sloupců, výchozí je true (lze nechat false a jen povolit na některých sloupcích pomocí sortable). Pokud je true, je možné řadit dle sloupců. Pak se na sloupcích určuje povolení řazení dle typu, případně dle parametru sortable.
   */
  sortable?: boolean;

  /**
   * Zda u tabulky povolit na sloupcích seskupení, výchozí je true (lze nechat false a jen povolit na některých sloupcích pomocí groupable). Pokud je true, je možné seskupit dle sloupců. Pak se na sloupcích určuje povolení seskupení dle typu, případně dle parametru groupable.
   */
  groupable?: boolean;

  /**
   * Zda se v titulku tabulky má zobrazit ovládací prvek pro ukládání a přepínání pohledů
   * dává smysl, pouze pokud je tabulce předáván title a settingsKey (jinak se rovněž nezobrazuje)
   */
  disablePohled?: boolean;

  /**
   * Má se u sloupců zobrazovat filtr pro hodnoty ve sloupci? Implicitně je false.
   */
  columnsFilterable?: boolean;

  /**
   * Výchozí hodnoty, pokud není pro daný klíč žádný filtr aktivní.
   */
  initialConfig?: RowsDataConfig;
};

export type FinalClientTableConfig<
  DATA extends IData<ID>,
  FILTER,
  ID,
> = CommonFinalTableConfig<DATA, FILTER, ID> & {};

export type FinalTableConfig<
  DATA extends IData<ID>,
  FILTER,
  ID,
> = CommonFinalTableConfig<DATA, FILTER, ID> & {
  /**
   * Api pro načtení dat pro tabulku.
   */
  api: (request: DataRequestVO<FILTER>) => Promise<DataResponseVO<DATA>>;

  /**
   * Výchozí typ tabulky a její práce s daty.
   */
  type?: FinalTableType;
};

export type FinalTableSettings = {
  profileData?: FinalTableProfileData;
  filterData?: FinalTableFilterData;
};

export type FinalTableProfiles = {
  profiles: FinalTableProfileType[];
  activeProfile?: FinalTableProfileType;
};

export type FinalTableProfileType = {
  id: string;
  nazev: string;
  profile: FinalTableProfileData;
};

export type FinalTableFilterData = {
  visibleFields?: string[];
};

export type FinalTableProfileData = {
  /**
   * Řazení.
   */
  sort: DataSortVO[];
  /**
   * Seskupení dle sloupců.
   */
  grouping: DataGroupVO[];
  /**
   * Agregace na sloupcích.
   */
  aggregate: DataAggregateVO[];
  /**
   * Zobrazení patičky.
   */
  showFooter: boolean;
  /**
   * Šířka sloupce kde jsou seskupené záznamy.
   */
  groupColumnWidth?: number;
  /**
   * Info o sloupcích (zda jsou vidět, šířka apod.).
   */
  columns: ColumnInfo[];
  /**
   * Omezení řádků dle hodnot ve sloupcích (např. uživatel zaškrtne které hodnoty ve sloupci chce vidět).
   */
  fieldValuesFilter: FieldValuesFilterData;
};

export type FinalTableViewType = {
  id: string;
  nazev: string;
  view: FinalTableViewData;
};

export type FinalTableViewData = {
  filter: Record<string, unknown>;
  profile: FinalTableProfileType['id'] | null;
};

export type FinalTableViews = {
  views: FinalTableViewType[];
  activeView?: FinalTableViewType;
};
